import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSyncAlt, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import {
  AppRouteUi,
  CallStatusType,
  CallType,
} from "../../configs";
import { ActionUi, SocketContext, useAppSelector, VRIAppStateType } from "../../core";
import { Option } from "../../models";
import { useFunctionalityOperator } from "../../pages/hooks";
import { useFunctionalityCalls } from "../../pages/hooks/functionalityCalls";
import { apiPolling, logger } from "../../utils";
import { ConferenceAddOperator } from "../../models/api/Call";
import { Call } from "@twilio/voice-sdk";
import { ActionAudioCall } from "../../core";

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  callType: CallType;
  callinfo: Call;
  isLanguageTransferEnabled?: boolean;
};
export function ModalAddOPIOperatorParticipant(props: Props): JSX.Element {
  const currentUserCompanyId = useAppSelector(
    (state: VRIAppStateType) => state.auth.profile?.companyId
  );
  let routeParamRoomId = AppRouteUi.VideoCall.Room.useParam();
  if (!routeParamRoomId) {
    routeParamRoomId = AppRouteUi.VideoConference.Room.useParam();
  }
  // const { onUpdate } =
  //   useFunctionalityCalls();


  const dispatch = useDispatch();

  const [selectedUserId, setSelectedUserId] = React.useState<any>();
  const [selectedUserName, setSelectedUserName] = React.useState<any>();
  const [selectedLanguage, setSelectedLanguage] = useState<Option | null>(null);
  const [languageSearchText, setLanguageSearchText] = useState("");
  const [languageOptions, setLanguageOptions] = useState<Option[]>([]);
  const [isLanguageLoading, setIsLanguageLoading] = useState(false);
  const {
    onAddOperatorConferenceOPI,
    onSearchLanguagesOPICall,
    onFilterOperatorOPICallByLanguage,
  } = useFunctionalityCalls();
  const { inCallOPIOperatorList } = useFunctionalityOperator({});
  const [loading, setLoading] = useState(false);
  const refreshLanguageOperators = useRef<(() => void) | null>(null);
  const [apiUserNames, setApiUserNames] = useState<Option[]>([]);
  const currentUserId = useAppSelector((state: VRIAppStateType) => state.auth.profile?.id);
  const filteredOperatorList = apiUserNames.filter(
    (user) => user.value !== currentUserId
  );
  const data = useAppSelector((state: VRIAppStateType) => ({
    callerId: state.auth.profile?.id,
    //roomId: state.ui.roomId,
    chatSid: state.ui.chatSid,
    audioOperatorCalls: state.audioCall.audioOperatorCalls
  }));
  const mapToOption = (apiUserNames: any) => {
    if (!Array.isArray(apiUserNames)) return [];

    return apiUserNames
      .map((item: any) => ({
        value: item?.value ?? item?.id,
        label: item?.label ?? item?.name,
      }))
      .filter((item: Option) => item.value !== undefined && !!item.label);
  };

  const isLanguageTransferFlow = props.isLanguageTransferEnabled === true;
  const callSid = props.callinfo.customParameters?.get("CallSid") ?? "";
  const filteredLanguageOptions = languageOptions.filter((language) =>
    language.label.toLowerCase().includes(languageSearchText.trim().toLowerCase())
  );
  const shouldShowLanguageDropdownIndicator =
    !selectedLanguage &&
    !!languageSearchText.trim() &&
    filteredLanguageOptions.length > 0;

  useEffect(() => {
    console.log("callinfo:", props.callinfo);
  }, [props.callinfo]);

  useEffect(() => {
    if (!isLanguageTransferFlow || !callSid) return;

    let isCancelled = false;
    setIsLanguageLoading(true);

    onSearchLanguagesOPICall(callSid)
      .then((languages) => {
        if (!isCancelled) {
          setLanguageOptions(mapToOption(languages));
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setLanguageOptions([]);
          toast.error("Failed to load languages");
        }
      })
      .finally(() => {
        if (!isCancelled) setIsLanguageLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [isLanguageTransferFlow, callSid, onSearchLanguagesOPICall]);


  useEffect(() => {
    let timeIntervalId: number;
    if (!props.show || isLanguageTransferFlow) return;

    const fetchOperators = async () => {
      setLoading(true);
      try {
        const res = await inCallOPIOperatorList(
          props.callinfo.customParameters?.get("CallSid") ?? ""
        );
        setApiUserNames(res.data);
      } catch (e) {
        console.error("Failed to fetch operators", e);
        toast.error("Failed to load operators");
      } finally {
        setLoading(false);
      }
    };

    fetchOperators();

    timeIntervalId = apiPolling(fetchOperators, 30000);

    return () => {
      timeIntervalId && clearInterval(timeIntervalId);
    };
  }, [props.show, props.callinfo, isLanguageTransferFlow]);


  const onModalClose = () => {
    props.handleShow(false);
    setSelectedUserId("");
    setSelectedUserName("");
    if (isLanguageTransferFlow) {
      setSelectedLanguage(null);
      setLanguageSearchText("");
      setApiUserNames([]);
    }
  };

  useEffect(() => {
    if (!props.show || !isLanguageTransferFlow || !selectedLanguage || !callSid) {
      if (isLanguageTransferFlow) setLoading(false);
      return;
    }

    let cancelled = false;
    let inFlight = false;
    const fetchOperators = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      setLoading(true);
      try {
        const operators = await onFilterOperatorOPICallByLanguage(
          callSid,
          String(selectedLanguage.value)
        );
        if (!cancelled) setApiUserNames(mapToOption(operators));
      } catch (e) {
        if (!cancelled) {
          console.error("Failed to fetch language-filtered operators", e);
          toast.error("Failed to load operators");
        }
      } finally {
        inFlight = false;
        if (!cancelled) setLoading(false);
      }
    };

    refreshLanguageOperators.current = fetchOperators;
    void fetchOperators();
    const intervalId = window.setInterval(fetchOperators, 10000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      refreshLanguageOperators.current = null;
    };
  }, [props.show, isLanguageTransferFlow, selectedLanguage, callSid, onFilterOperatorOPICallByLanguage]);

  const onLanguageChange = (language: Option | null) => {
    setSelectedLanguage(language);
    setLanguageSearchText(language?.label ?? "");
    setSelectedUserId("");
    setSelectedUserName("");
    setApiUserNames([]);
  };

  const callOperator = () => {
    const value: ConferenceAddOperator = {
      callSid: props.callinfo.customParameters?.get("CallSid") || "",
      called: props.callinfo.parameters?.From,
      operatorsID: selectedUserId,
      languageId:
        isLanguageTransferFlow && selectedLanguage
          ? String(selectedLanguage.value)
          : null,
    };

    onAddOperatorConferenceOPI(value).then((a) => {
      console.log("operator outbound redial performed");
      dispatch(ActionAudioCall.UpsertOperatorConferenceCall({
        operatorId: selectedUserId,
        operatorName: selectedUserName,
        operatorStatus: { current: CallStatusType.Initiated }
      })
      );

    });


  };

  return (
    <Modal
      show={props.show}
      onHide={() => onModalClose()}
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title> Add to Call</Modal.Title>
      </Modal.Header>

      {isLanguageTransferFlow ? (
        <>
          <Modal.Body>
            <form>
              <div className="form-row">
                <div className="form-group col-md-12">
                  <label htmlFor="" className="text-muted font-sz-12">
                    Language
                  </label>
                  <Select
                    components={{ IndicatorSeparator: () => null }}
                    value={selectedLanguage}
                    inputValue={selectedLanguage ? "" : languageSearchText}
                    options={filteredLanguageOptions}
                    isLoading={isLanguageLoading}
                    isClearable
                    isSearchable
                    styles={{
                      dropdownIndicator: (base) => ({
                        ...base,
                        display: shouldShowLanguageDropdownIndicator
                          ? "flex"
                          : "none",
                      }),
                    }}
                    placeholder="Type a language..."
                    menuIsOpen={
                      !!languageSearchText.trim() && !selectedLanguage
                    }
                    noOptionsMessage={() =>
                      languageSearchText.trim()
                        ? "No languages found"
                        : null
                    }
                    onInputChange={(value, action) => {
                      if (action.action !== "input-change") return;
                      if (selectedLanguage && value.trim()) {
                        setSelectedLanguage(null);
                        setSelectedUserId("");
                        setSelectedUserName("");
                        setApiUserNames([]);
                      }
                      setLanguageSearchText(value);
                    }}
                    onChange={onLanguageChange}
                  />
                </div>
                <div className="form-group col-md-12">
                  <label htmlFor="" className="text-muted font-sz-12">
                    Operator
                  </label>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <Select
                        components={{ IndicatorSeparator: () => null }}
                        options={filteredOperatorList}
                        value={filteredOperatorList.find((operator) => operator.value === selectedUserId) ?? null}
                        isDisabled={!selectedLanguage || loading}
                        isLoading={loading}
                        placeholder={
                          selectedLanguage
                            ? "Select an Operator..."
                            : "Select a language first..."
                        }
                        noOptionsMessage={() =>
                          selectedLanguage && !loading
                            ? "No interpreter found"
                            : null
                        }
                        onChange={(value) => {
                          setSelectedUserId(value?.value);

                          if (!!value?.value) {
                            setSelectedUserName(value?.label);
                            dispatch(ActionUi.SetReceiverName(value?.label));
                            dispatch(ActionUi.SetReceiverId(value?.value));
                          }
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-secondary ml-2 d-flex align-items-center justify-content-center"
                      style={{ width: "38px", height: "38px", flexShrink: 0, padding: 0 }}
                      disabled={!selectedLanguage || !callSid || loading}
                      onClick={() => refreshLanguageOperators.current?.()}
                      aria-label={loading ? "Refreshing operators" : "Refresh operators"}
                      aria-busy={loading}
                      title="Refresh operators"
                    >
                      <FontAwesomeIcon icon={loading ? faSpinner : faSyncAlt} spin={loading} />
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </Modal.Body>

          <Modal.Footer>
            <div
              className="form-row
                justify-content-lg-end justify-content-xl-end justify-content-md-end justify-content-sm-center justify-content-center
                flex-column flex-sm-column flex-md-row flex-lg-row flex-xl-row
                mx-0"
            >
              <SocketContext.Consumer>
                {(connection) => (
                  <button
                    disabled={!filteredOperatorList.some((operator) => operator.value === selectedUserId)}
                    className="  btn btn-outline-dark border-grey
                      d-flex d-sm-flex d-md-inline-block d-lg-inline-block d-xl-inline-block
                      flex-grow-1 flex-sm-grow-1 flex-xl-grow-0 flex-lg-grow-0 flex-md-grow-0
                      justify-content-center align-items-center my-xl-0 my-lg-0 my-md-0 my-sm-2 my-2 "
                    onClick={() => {
                      logger({ message: `Pressed button to add call: ${selectedUserName} (${selectedUserId})` });
                      callOperator();
                      props.handleShow(false);
                    }}
                  >
                    Add to Call
                  </button>
                )}
              </SocketContext.Consumer>
            </div>
          </Modal.Footer>
        </>
      ) : loading ? (
        <div className="p-4 text-center">
          <h4 className="p-2 text-center">Fetching Interpreters...</h4>
        </div>
      ) : filteredOperatorList?.length > 0 ? (
        <>

          <Modal.Body>
            <form>
              <div className="form-row">
                <div className="form-group col-md-12">
                  <label htmlFor="" className="text-muted font-sz-12">
                    Operator
                  </label>

                  <Select
                    components={{
                      IndicatorSeparator: () => null,
                    }}
                    options={filteredOperatorList}
                    placeholder="Select an Operator..."
                    onChange={(value) => {
                      setSelectedUserId(value?.value);

                      if (!!value?.value) {
                        setSelectedUserName(value?.label);
                        dispatch(ActionUi.SetReceiverName(value?.label));
                        dispatch(ActionUi.SetReceiverId(value?.value));
                      }
                    }}
                  />
                </div>
              </div>
            </form>
          </Modal.Body>

          <Modal.Footer>
            <div
              className="form-row 
                justify-content-lg-end justify-content-xl-end justify-content-md-end justify-content-sm-center justify-content-center 
                flex-column flex-sm-column flex-md-row flex-lg-row flex-xl-row
                mx-0"
            >
              <SocketContext.Consumer>
                {(connection) => (
                  <button
                    disabled={(!selectedUserId && !selectedUserName)}
                    className="  btn btn-outline-dark border-grey 
                      d-flex d-sm-flex d-md-inline-block d-lg-inline-block d-xl-inline-block 
                      flex-grow-1 flex-sm-grow-1 flex-xl-grow-0 flex-lg-grow-0 flex-md-grow-0 
                      justify-content-center align-items-center my-xl-0 my-lg-0 my-md-0 my-sm-2 my-2 "
                    onClick={(event) => {
                      logger({ message: `Pressed button to add call: ${selectedUserName} (${selectedUserId})` });
                      // videoConnect(connection.socketConnection, event);
                      callOperator();
                      props.handleShow(false);
                    }}
                  >
                    Add to Call
                  </button>
                )}
              </SocketContext.Consumer>
            </div>
          </Modal.Footer>
        </>

      ) : (
        <div className="p-4 text-center">
          <h4 className="p-2 text-center">No interpreter found</h4>
        </div>
      )}

    </Modal>
  );
}
